import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { IActivePoint } from '../../_checkout/_hooks/useDelivery';
import { shallowEqual, useSelector } from 'react-redux';
import { IRedux, ISavedBranch } from '@/_store/types';
import { useSaveBranchData } from '@/_hooks/useSaveBranchData';
import { useTranslations } from 'next-intl';
import useDebounce from '@/shared/hooks/useDebounce';
import { branchService } from '@/_service/branch.service';
import getComputedPrice, { IComputePayload } from '@/_service/fare.service';
import dayjs from 'dayjs';
import { IComputeData } from '@/_types';
import { toaster } from '@/components/ui/toaster';

const COMPUTE_DEBOUNCE_MS = 400;

export default function useDelivery(totalPrice: number, shipperId: string) {
  const [activePoint, setActivePoint] = useState<IActivePoint | null>(null);
  const [claimId, setClaimId] = useState<string>();
  const [loading, setLoading] = useState(false)
  const [computeData, setComputeData] = useState<IComputeData | null>(null);
  const [deliveryPrice, setDeliveryPrice] = useState(0);
  const [branch, setBranch] = useState<ISavedBranch | null>(null);
  const [hasActiveBranch, setHasActiveBranch] = useState(false);

  const { user, cartState, settings, common, checkout, session } = useSelector(
    (state: IRedux) => ({
      user: state.auth.user,
      cartState: state.cart,
      settings: state.settings,
      common: state.common,
      checkout: state.checkout,
      session: state.session,
    }),
    shallowEqual
  );
  const { platform_id, order_type } = session;
  const saveBranch = useSaveBranchData();

  const storeBranch = common?.branches?.[shipperId]

  // Cart for the selected brand only (compute-price is per shipper)
  const cart = useMemo(() => {
    if (!cartState?.cart || !shipperId) return [];
    const brandCart = cartState.cart[shipperId];
    return brandCart?.products ?? [];
  }, [cartState?.cart, shipperId]);

  const t = useTranslations();
  const debouncedPrice = useDebounce(totalPrice, 300);

  const findNearestBranch = useCallback(
    async ({ lat, long }: { lat: number; long: number }) => {
      try {
        const result = await branchService.findNearest(
          {
            lat,
            long,
            order_type_id: order_type?.id,
            delivery_zone_id: common?.deliveryZones?.[shipperId]?.id || '',
          },
          shipperId
        );

        if (result?.data?.branches) {
          for (let nearestBranch of result.data?.branches) {
            if (nearestBranch?.is_active) {
              return nearestBranch;
            }
          }
        }
      } catch (error) {
        console.log(error);
        return;
      }
    },
    [order_type?.id, common?.deliveryZones?.[shipperId]?.id, shipperId]
  );

  // Set delivery details & Get delivery price
  useEffect(() => {
    if (order_type?.type == 'delivery') {
      if (common?.points.length > 0) {
        for (const point of common?.points) {
          if (
            point.isActive &&
            point.location?.[0] !== activePoint?.lat &&
            point.location?.[1] !== activePoint?.long
          ) {
            const newActivePoint = {
              address: point?.address,
              lat: point?.location?.[0] || 0,
              long: point?.location?.[1] || 0,
            };
            if (
              newActivePoint.lat !== activePoint?.lat ||
              newActivePoint.long !== activePoint?.long
            ) {
              setActivePoint(newActivePoint);
            }
            break;
          }
        }
      } else {
        setActivePoint(null);
      }
    }
  }, [common?.points, order_type?.type]);

  const findFutureNearestBranch = useCallback(
    async ({
      lat,
      long,
      date_time,
    }: {
      lat: number;
      long: number;
      date_time: string;
    }) => {
      try {
        const result = await branchService.findNearestByTime({
          lat,
          long,
          order_type_id: order_type?.id,
          date_time: date_time,
        }, shipperId);

        if (result?.data?.branches) {
          for (let nearestBranch of result.data?.branches) {
            if (nearestBranch?.is_active) {
              return nearestBranch;
            }
          }
        }
      } catch (error) {
        console.log(error);
        return;
      }
    },
    [order_type?.id]
  );

  const fetchNearestBranch = async () => {
    if (activePoint?.lat && activePoint?.long) {
      let nearestBranch;
      if (checkout?.future_time) {
        nearestBranch = await findFutureNearestBranch({
          ...activePoint,
          date_time: checkout?.future_time,
        });
      } else {
        nearestBranch = await findNearestBranch(activePoint);

        if (
          storeBranch?.id &&
          nearestBranch?.id &&
          storeBranch?.id !== nearestBranch?.id
        ) {
          // toaster.create({
          //   description: t('branch_switched_from_a_to_b', {
          //     a: storeBranch?.name,
          //     b: nearestBranch?.name,
          //   }),
          //   type: 'info',
          // });
        } else if (nearestBranch?.id && order_type?.type !== 'self-pickup') {
          // toaster.create({
          //   description: t('order_will_be_delivered_from_b', {
          //     b: nearestBranch?.name,
          //   }),
          //   type: 'info',
          // });
        }
      }

      if (nearestBranch && nearestBranch?.id) {
        setBranch(nearestBranch);
        saveBranch(nearestBranch);
        if (!hasActiveBranch) setHasActiveBranch(true);
        return;
      } else {
        setHasActiveBranch(false);
      }
    }
  };

  useEffect(() => {
    if (
      !settings.data?.choose_delivery_branch &&
      order_type?.type === 'delivery'
    ) {
      fetchNearestBranch();
    }
  }, [
    storeBranch?.id,
    activePoint?.lat,
    activePoint?.long,
    order_type?.type,
    settings.data?.choose_delivery_branch,
    checkout?.future_time,
  ]);

  const selectedBranch = useMemo(() => {
    return settings.data?.choose_delivery_branch
      ? branch || storeBranch
      : branch;
  }, [settings.data?.choose_delivery_branch, branch, storeBranch]);

  // Single stable key for all compute-price inputs – only re-run when this changes
  const computeRequestKey = useMemo(() => {
    if (
      order_type?.type !== 'delivery' ||
      !selectedBranch?.id ||
      activePoint?.lat == null ||
      activePoint?.long == null ||
      Number(totalPrice) <= 0
    ) {
      return '';
    }
    const cartKey =
      cart
        ?.map((p) => `${p.product_id}:${p.quantity}`)
        .sort()
        .join(',') ?? '';
    return [
      selectedBranch.id,
      activePoint.lat.toFixed(5),
      activePoint.long.toFixed(5),
      totalPrice,
      common?.deliveryZones?.[shipperId]?.id ?? '',
      cartKey,
      checkout?.payment_type?.id ?? '',
      checkout?.future_time ?? '',
    ].join('|');
  }, [
    order_type?.type,
    selectedBranch?.id,
    activePoint?.lat,
    activePoint?.long,
    totalPrice,
    common?.deliveryZones?.[shipperId]?.id,
    cart,
    checkout?.payment_type?.id,
    checkout?.future_time,
  ]);

  const lastComputeKeyRef = useRef<string>('');
  const computeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const shouldCompute =
      (settings.data?.choose_delivery_branch || hasActiveBranch) &&
      order_type?.type === 'delivery' &&
      computeRequestKey !== '';

    if (!shouldCompute) {
      lastComputeKeyRef.current = '';
      return;
    }

    // Skip if same request key (avoid duplicate calls from deps churn)
    if (computeRequestKey === lastComputeKeyRef.current) {
      return;
    }

    const runCompute = async () => {
      const branchToUse = selectedBranch;
      if (
        !branchToUse?.id ||
        !activePoint?.lat ||
        !activePoint?.long ||
        !shipperId
      ) {
        return;
      }
      lastComputeKeyRef.current = computeRequestKey;
      setLoading(true);
      try {
        console.log('deliveryZones', common?.deliveryZones, shipperId)
        const payload: IComputePayload = {
          branch_id: branchToUse.id,
          lat: activePoint.lat,
          long: activePoint.long,
          order_price: totalPrice,
          delivery_zone_id: common?.deliveryZones?.[shipperId]?.id || '',
          order_details: {
            client_name: user?.name || '',
            client_phone_number: user?.phone || '',
            description: '',
            extra_phone_number: '',
            future_time: checkout?.future_time
              ? dayjs(checkout.future_time).format('YYYY-MM-DD HH:mm:ss')
              : undefined,
            payment_type_id: checkout?.payment_type?.id ?? '',
            to_address: activePoint?.address || '',
            to_location: { lat: activePoint.lat, long: activePoint.long },
            branch_id: branchToUse.id,
            products:
              cart?.map((el) => ({
                price: el?.price ?? 0,
                name: el?.product_name ?? '',
                product_id: el.product_id,
                quantity: el.quantity,
              })) ?? [],
            source_id: platform_id ?? '',
          },
        };
        const res = await getComputedPrice(payload, shipperId);
        if (res?.data) {
          setClaimId(res?.data?.external_delivery_id);
          setDeliveryPrice(res.data.price);
          setComputeData(res.data);
        }
      } catch (err) {
        lastComputeKeyRef.current = ''; // allow retry
        toaster.create({
          title: t('error_while_computing_delivery_cost'),
          type: 'error',
        });
      } finally {
        setLoading(false);
      }
    };

    computeTimeoutRef.current = setTimeout(runCompute, COMPUTE_DEBOUNCE_MS);

    return () => {
      if (computeTimeoutRef.current) {
        clearTimeout(computeTimeoutRef.current);
        computeTimeoutRef.current = null;
      }
    };
  }, [
    computeRequestKey,
    settings.data?.choose_delivery_branch,
    hasActiveBranch,
    order_type?.type,
  ]);

  return { deliveryPrice, loading, hasActiveBranch };
}